import type { AccommodationDayView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import { MAX_ACCOMMODATION_CALENDAR_MONTHS } from 'constants/calendar'
import { fillArray } from 'lib/array/arrayUtils'
import { addMonths, beginningOfToday, formatIsoDate } from 'lib/datetime/dateUtils'

export interface FeaturedDateRange {
  // Type of featured day
  type: 'exclusive' | 'special' | 'loyalty'
  // Booking satisfies this date range if check-in is on or after this date, in ISO 8601
  startIsoDate: string
  // Booking satisfies this date range if check-out is on or before this date, in ISO 8601
  endIsoDate: string
  // If set, indicates pricing information (if available) is a minimum per-night average for this
  // length of stay
  marketedLos?: number
  // If set, indicates pricing information (if available) is a minimum per-night average for this
  // occupancy
  marketedOccupancy?: Pick<App.RoomCapacity, 'adults' | 'teenagers' | 'children' | 'infants'>
}

export function generateStaticDayViews(
  timezone?: string,
  featuredDateRanges?: Utils.NonEmptyArray<FeaturedDateRange> | undefined,
  dayPrices?: { [isoDate: string]: App.Property.CalendarDay } | undefined,
  displayLuxPlusPrice?: boolean,
): Array<AccommodationDayView> {
  const calendarStartDate = beginningOfToday(timezone)
  return fillArray(MAX_ACCOMMODATION_CALENDAR_MONTHS).flatMap((monthIndex) => {
    const month = addMonths(calendarStartDate, monthIndex)
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const daysLeftInMonth = monthIndex === 0 ? daysInMonth - month.getDate() + 1 : daysInMonth

    return fillArray(
      daysLeftInMonth,
      dayIndex => monthIndex === 0 ? dayIndex + month.getDate() : dayIndex + 1,
    ).map((day): AccommodationDayView => {
      const date = new Date(month.getFullYear(), month.getMonth(), day)
      const isoDate = formatIsoDate(date)
      const dayPrice = dayPrices?.[isoDate]
      const canDisplayLuxPlusPrice = !!(displayLuxPlusPrice && dayPrice?.campaignLuxPlusDisplayPrice)
      const pricing = dayPrice ? {
        price: canDisplayLuxPlusPrice ? dayPrice.campaignLuxPlusDisplayPrice! : dayPrice.campaignDisplayPrice,
        fees: [],
        saleUnit: 'night',
      } : undefined
      const priceType = canDisplayLuxPlusPrice ? 'member' : 'default'
      const featured = featuredDateRanges?.find(({ startIsoDate, endIsoDate }) => startIsoDate <= isoDate && isoDate <= endIsoDate)?.type
      return {
        date: isoDate,
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        pricing,
        soldOut: false,
        priceType,
        durationUnit: 'night',
        featured,
      }
    })
  })
}
